import React, {
  useState,
  forwardRef,
  useRef,
  useEffect,
  useImperativeHandle,
  useCallback,
} from "react";
import {
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Divider,
  Slider,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/Delete";
import SignatureCanvas from "react-signature-canvas";
import { post, postFormData } from "../apis/apis";
import toastAlert from "./toastAlert";
// import toastAlert from "@components/toastAlert";

const DrawCanvas = forwardRef(({
  user_id_user,
  user_email,
  saveSignature,
  modalClose,
  initialsBox,
  modalClosingD,
  onProfileSaved,
  receiverSide,
  primaryColorComp,
  existingSignatureUrl,
  penColor: penColorProp,
  defaultSaveAsProfile,
  onDefaultSaveApplied,
}, ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isLandscape = useMediaQuery('(orientation: landscape)');

  const [color, setColor] = useState("black");
  const colorToUse = penColorProp !== undefined && penColorProp !== null ? penColorProp : color;
  const [thickness, setThickness] = useState(8);
  const [loader, setLoader] = useState(false);
  const sigCanvas = useRef(null);
  const [prevSignatures, setPrevSignatures] = useState([]);
  const [prevModalOpen, setPrevModalOpen] = useState(false);
  const [saveAsProfile, setSaveAsProfile] = useState(false);
  const hasAppliedDefaultSaveRef = useRef(false);
  const loadedSignatureUrlRef = useRef(null); // Track loaded signature URL
  const isResizingRef = useRef(false); // Track if resize is in progress
  const isInitializedRef = useRef(false); // Track if signature pad is initialized

  useEffect(() => {
    // Fetch stored signatures (replace with actual API call)
    setPrevSignatures([]);
  }, []);

  useEffect(() => {
    if (defaultSaveAsProfile && !hasAppliedDefaultSaveRef.current) {
      setSaveAsProfile(true);
      hasAppliedDefaultSaveRef.current = true;
      onDefaultSaveApplied?.();
    }
    if (!defaultSaveAsProfile) {
      hasAppliedDefaultSaveRef.current = false;
    }
  }, [defaultSaveAsProfile, onDefaultSaveApplied]);

  // Function to load image onto canvas (reusable)
  const loadImageOntoCanvas = useCallback((imageUrl) => {
    if (!imageUrl || !sigCanvas.current) {
      return;
    }

    let retryCount = 0;
    const maxRetries = 10; // Limit retries to prevent infinite loop

    const attemptLoad = () => {
      const canvas = sigCanvas.current?.getCanvas();
      if (!canvas || canvas.width === 0 || canvas.height === 0 || isResizingRef.current) {
        // Canvas not ready yet or resize in progress, retry after a short delay
        retryCount++;
        if (retryCount < maxRetries) {
          setTimeout(attemptLoad, 50);
        }
        return;
      }

      const img = new Image();
      img.crossOrigin = "anonymous"; // Handle CORS if needed

      img.onload = () => {
        // Verify sigCanvas and canvas are still valid (component might have unmounted)
        if (!sigCanvas.current || !canvas || canvas.width === 0 || canvas.height === 0) {
          return;
        }

        const ctx = canvas.getContext("2d");
        if (!ctx) {
          return;
        }

        // Clear the canvas first
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Calculate scaling to fit image within canvas while maintaining aspect ratio
        const imgAspect = img.width / img.height;
        const canvasAspect = canvas.width / canvas.height;

        let drawWidth, drawHeight, drawX, drawY;

        if (imgAspect > canvasAspect) {
          // Image is wider - fit to width
          drawWidth = canvas.width;
          drawHeight = canvas.width / imgAspect;
          drawX = 0;
          drawY = (canvas.height - drawHeight) / 2;
        } else {
          // Image is taller - fit to height
          drawWidth = canvas.height * imgAspect;
          drawHeight = canvas.height;
          drawX = (canvas.width - drawWidth) / 2;
          drawY = 0;
        }

        // Draw the image onto the canvas
        ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);

        // Update the signature pad's internal state
        // This ensures the signature pad recognizes the drawn content
        // Check if sigCanvas.current is still valid before accessing
        if (sigCanvas.current) {
          const signaturePad = sigCanvas.current.getSignaturePad();
          if (signaturePad) {
            // Mark as not empty since we've drawn something
            signaturePad._isEmpty = false;
          }
        }

        // Store the loaded signature URL
        loadedSignatureUrlRef.current = imageUrl;
      };

      img.onerror = (error) => {
        console.error("Error loading signature image:", error);
        loadedSignatureUrlRef.current = null;
      };

      img.src = imageUrl;
    };

    // Start loading immediately, with minimal delay only if needed
    setTimeout(attemptLoad, 50);
  }, []);

  // Load existing signature image onto canvas when editing
  useEffect(() => {
    if (existingSignatureUrl) {
      loadedSignatureUrlRef.current = existingSignatureUrl;
      // Wait a bit longer for canvas to be fully initialized
      const timeoutId = setTimeout(() => {
        loadImageOntoCanvas(existingSignatureUrl);
      }, 300);
      return () => clearTimeout(timeoutId);
    } else {
      // Clear canvas when no existing signature URL
      if (sigCanvas.current) {
        const canvas = sigCanvas.current.getCanvas();
        if (canvas) {
          const ctx = canvas.getContext("2d");
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          const signaturePad = sigCanvas.current?.getSignaturePad();
          if (signaturePad) {
            signaturePad.clear();
            signaturePad._isEmpty = true;
          }
        }
      }
      loadedSignatureUrlRef.current = null;
    }
  }, [existingSignatureUrl, loadImageOntoCanvas]);
  //   useEffect(() => {
  //     if (sigCanvas.current) {
  //       sigCanvas.current.penColor = color;
  //       sigCanvas.current.dotSize = thickness;
  //     }
  //   }, [color, thickness]);
  const [imageDataURL, setImageDataURL] = useState(null);

  // Add the convertLinesToImage function
  const convertLinesToImage = async () => {
    if (sigCanvas.current) {
      const canvas = sigCanvas.current.getCanvas();
      const ctx = canvas.getContext("2d");

      // Save the current canvas state
      ctx.save();

      // Make the canvas transparent
      canvas.style.backgroundColor = "rgba(0, 0, 0, 0)";

      // Get the signature data URL
      const dataURL = canvas.toDataURL("image/png");

      // Restore the canvas state
      ctx.restore();

      // Set the canvas background color back
      canvas.style.backgroundColor = "white";

      return dataURL;
    }
    return null;
  };
  const base64toFile = (base64String, filename) => {
    const arr = base64String.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };
  const handleSave = async () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      setLoader(true);
      const dataURL = await convertLinesToImage();
      if (dataURL) {
        if (saveAsProfile) {
          if (!user_id_user) {
            toastAlert("error", "Cannot save to profile: user not identified. Reload the document and try again.");
          } else {
            try {
              const filename = "signature.png";
              const fileObject = base64toFile(dataURL, filename);
              const postData = {
                image: fileObject,
                user_id: user_id_user,
              };
              const apiData = await postFormData(postData);
              if (apiData && apiData.error) {
                toastAlert("error", apiData.message || "Error uploading signature.");
              } else if (apiData && apiData.public_url) {
                const ImageUrl = apiData.public_url;
                const TypeCheck = initialsBox ? "profile_initils" : "profile";
                const saveSignatureData = {
                  user_id: user_id_user,
                  signature_image_url: ImageUrl,
                  type: TypeCheck,
                };
                const saveResult = await post("user/AddUserSignaturesToDb", saveSignatureData);
                if (saveResult && (saveResult.error === true || saveResult.status === false)) {
                  toastAlert("error", saveResult.message || saveResult.error || "Failed to save signature to profile.");
                } else {
                  toastAlert("success", "Signature saved to profile.");
                  onProfileSaved?.();
                }
              } else {
                toastAlert("error", "Upload did not return a valid URL.");
              }
            } catch (error) {
              toastAlert("error", error?.message || "Failed to save signature to profile.");
            }
          }
        }
        console.log("dataUrl", dataURL);
        saveSignature(dataURL);
        modalClose();
      }
      //   saveSignature(dataURL);
      setLoader(false);
    }
  };

  const handleClear = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
  };

  const parseColorToRgb = (color) => {
    if (color === "black") return { r: 0, g: 0, b: 0 };
    if (color === "blue") return { r: 0, g: 0, b: 255 };
    const hex = color.replace(/^#/, "");
    if (hex.length === 6 && /^[0-9A-Fa-f]{6}$/.test(hex)) {
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
      };
    }
    return { r: 0, g: 0, b: 0 };
  };

  const recolorCanvasTo = useCallback((newColor) => {
    if (!sigCanvas.current) return;
    const canvas = sigCanvas.current.getCanvas();
    if (!canvas || !canvas.width || !canvas.height) return;
    const ctx = canvas.getContext("2d");
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    const { r, g, b } = parseColorToRgb(newColor);
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] !== 0) {
        data[i] = r; data[i + 1] = g; data[i + 2] = b;
      }
    }
    ctx.putImageData(imageData, 0, 0);
  }, []);

  useImperativeHandle(ref, () => ({
    handleSave,
    handleClear,
    recolorTo: recolorCanvasTo,
  }), [handleSave, handleClear, recolorCanvasTo]);

  const handleColorChange = (newColor) => {
    setColor(newColor);
    recolorCanvasTo(newColor);
  };
  const handleThicknessChange = (event, newValue) => {
    setThickness(newValue);
    // if (sigCanvas.current) {
    //   sigCanvas.current.dotSize = newValue;
    //   // Redraw the existing signature with new thickness
    //   const currentData = sigCanvas.current.toData();
    //   sigCanvas.current.clear();
    //   sigCanvas.current.fromData(currentData);
    // }
  };

  // Ensure signature pad is ready for drawing after initial mount
  useEffect(() => {
    if (!sigCanvas.current || isInitializedRef.current) return;

    // Wait for canvas to be ready
    const initSignaturePad = () => {
      const canvas = sigCanvas.current?.getCanvas();
      const signaturePad = sigCanvas.current?.getSignaturePad();

      if (canvas && canvas.width > 0 && canvas.height > 0 && signaturePad) {
        // Ensure signature pad is enabled and ready
        signaturePad.on();
        // Clear any initialization issues
        if (signaturePad.isEmpty()) {
          signaturePad._isEmpty = true;
        }
        // Mark as initialized
        isInitializedRef.current = true;
      } else {
        // Retry if not ready
        setTimeout(initSignaturePad, 100);
      }
    };

    // Start initialization after a delay to ensure DOM is ready
    setTimeout(initSignaturePad, 400);
  }, []);

  useEffect(() => {
    if (!sigCanvas.current) return;

    const canvas = sigCanvas.current.getCanvas();
    if (!canvas) return;

    const parent = canvas.parentNode;
    if (!parent) return;

    // Function to resize the canvas
    const resizeCanvas = () => {
      if (!sigCanvas.current || !canvas || !parent) return;

      // Mark resize as in progress
      isResizingRef.current = true;

      // Get the computed styles of the parent element
      const { width, height } = parent.getBoundingClientRect();

      // Store the loaded signature URL before resize (if any)
      const signatureToReload = loadedSignatureUrlRef.current;

      // Get signature pad before resize
      const signaturePad = sigCanvas.current?.getSignaturePad();

      // Store current drawing data if signature pad exists and is not empty
      let currentData = null;
      if (signaturePad && !signaturePad.isEmpty()) {
        try {
          currentData = signaturePad.toData();
        } catch (e) {
          console.log("Could not save signature data:", e);
        }
      }

      // Set the canvas width and height attributes
      canvas.width = width;
      canvas.height = height;

      // Reinitialize the signature pad after resize
      if (signaturePad) {
        // Only clear if there's no existing data to preserve
        if (!currentData) {
          signaturePad.clear();
        } else {
          // Clear first, then restore
          signaturePad.clear();
          try {
            signaturePad.fromData(currentData);
          } catch (e) {
            console.log("Could not restore signature data:", e);
            signaturePad.clear();
          }
        }

        // Ensure the signature pad is properly initialized and enabled
        signaturePad._isEmpty = currentData ? false : true;

        // Re-enable the signature pad to ensure event listeners are active
        signaturePad.on();

        // Ensure it's marked as initialized after resize
        isInitializedRef.current = true;
      }

      // Mark resize as complete
      isResizingRef.current = false;

      // Reload the signature if one was loaded before resize
      if (signatureToReload && loadImageOntoCanvas) {
        // Small delay to ensure canvas is ready after resize
        setTimeout(() => {
          loadImageOntoCanvas(signatureToReload);
        }, 150);
      }
    };

    // Initial resize with a small delay to ensure DOM is ready
    // Use a longer delay to ensure everything is ready before first resize
    const timeoutId = setTimeout(() => {
      resizeCanvas();
      // After initial resize, ensure signature pad is initialized
      setTimeout(() => {
        const signaturePad = sigCanvas.current?.getSignaturePad();
        if (signaturePad && !isInitializedRef.current) {
          signaturePad.on();
          isInitializedRef.current = true;
        }
      }, 100);
    }, 200);

    // Resize the canvas when the window resizes or orientation changes
    window.addEventListener("resize", resizeCanvas);
    window.addEventListener("orientationchange", () => {
      setTimeout(resizeCanvas, 200);
    });

    // Use ResizeObserver for more accurate parent size tracking
    let resizeObserver;
    if (window.ResizeObserver) {
      let resizeTimeout;
      resizeObserver = new ResizeObserver(() => {
        // Debounce resize to prevent too frequent calls
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          resizeCanvas();
        }, 150);
      });
      resizeObserver.observe(parent);
    }

    // Cleanup event listeners on component unmount
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", resizeCanvas);
      window.removeEventListener("orientationchange", resizeCanvas);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [initialsBox, loadImageOntoCanvas]);
  const handleDeleteSignature = (signatureId) => {
    // Replace with API call
    setPrevSignatures(prevSignatures.filter((sig) => sig.id !== signatureId));
    // toastAlert("success", "Signature Deleted Successfully!");
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        height: "100%",
        maxHeight: "100%",
        overflow: "hidden",
        flex: 1,
        minHeight: 0,
        position: "relative",
      }}
    >
      {/* Scrollable Canvas Area */}
      <Box
        sx={{
          flex: 1,
          width: "100%",
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minHeight: 0,
          maxHeight: "100%",
        }}
      >
        {/* Canvas Container */}
        <Box
          sx={{
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
            width: initialsBox
              ? { xs: "95%", sm: "30%", md: "30%" }
              : { xs: "100%", sm: "95%" },
            border: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            position: "relative",
            backgroundColor: '#e0e0e0',
            overflow: "hidden",
            minHeight: { xs: isLandscape ? "120px" : "120px", sm: "300px" },
            // maxHeight: { xs: isLandscape ? "calc(100vh - 280px)" : "calc(100vh -580px)", sm: "calc(100vh - 580px)" },
            mb: 0,
          }}
        >
          <SignatureCanvas
            ref={sigCanvas}
            penColor={colorToUse}
            dotSize={13}
            maxWidth={thickness}
            canvasProps={{
              className: "sigCanvas",
              style: {
                width: "100%",
                height: "100%",
                position: "absolute",
                top: 0,
                left: 0,
                touchAction: "none",
                WebkitTouchCallout: "none",
                WebkitUserSelect: "none",
                userSelect: "none",
              },
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: initialsBox ? "76%" : "86%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center",
              width: "80%",
              pointerEvents: "none",
              gap: 3,
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: "1px",
                background: "rgba(0,0,0,0.12)",
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: "-1px",
                  left: 0,
                  right: 0,
                  height: "2px",
                  background: "rgba(0,0,0,0.05)",
                  borderRadius: "1px",
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Save as profile signature (only when not receiver side) */}
      {!receiverSide && (
        <Box sx={{ flexShrink: 0, display: "flex", justifyContent: "center", alignItems: "center", width: "100%", px: 1, py: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={saveAsProfile}
                onChange={(e) => setSaveAsProfile(e.target.checked)}
                size="small"
                sx={{
                  color: "rgba(0, 0, 0, 0.6)",
                  "&.Mui-checked": {
                    color: "rgb(35, 179, 232)",
                  },
                }}
              />
            }
            label={
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: "13px", sm: "14px" },
                  color: "rgba(0, 0, 0, 0.7)",
                }}
              >
                Save as profile {initialsBox ? "initials" : "signature"}
              </Typography>
            }
          />
        </Box>
      )}

    </Box>
  );
});

DrawCanvas.displayName = "DrawCanvas";

export default DrawCanvas;
